// Formatting helpers for dates, durations and counts (design system 04-data §4). Pure and
// DOM-free so they can be unit-tested under node and reused by every page.
//
// Why en-GB everywhere: the design system asks for an unambiguous absolute date ("31 Jul 2026",
// never "07/31/26"); en-GB's default medium date is exactly that form, so the locale is fixed
// rather than inherited from the browser, where en-US would silently produce the ambiguous one.

export const LOCALE = 'en-GB';

/**
 * Why month names come from `en` rather than the display locale: recent ICU data abbreviates
 * September as "Sept" in en-GB, so the same date would render differently across Node and
 * browser versions. Taking the three-letter month from Intl's `en` data and assembling
 * day-month-year ourselves keeps the form the standard shows ("31 Jul 2026") and keeps it stable.
 */
const MONTH_LOCALE = 'en';

/** The em dash the design system reserves for "no value" (04-data §4). */
export const EM_DASH = '—';

/** Renders `value` or the em dash when it is null, undefined or empty. */
export function orDash(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return EM_DASH;
  if (typeof value === 'string' && value.trim() === '') return EM_DASH;
  return String(value);
}

/** The browser's IANA timezone, or UTC where the runtime does not expose one. */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export interface TimeFormatOptions {
  timeZone?: string;
  locale?: string;
}

function toDate(value: string | number | Date): Date | null {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function zone(options: TimeFormatOptions | undefined): string {
  return options?.timeZone ?? browserTimeZone();
}

/** `16 Sep 2026` in the given timezone. Invalid input renders the em dash rather than "Invalid Date". */
export function formatDate(value: string | number | Date | null | undefined, options?: TimeFormatOptions): string {
  if (value === null || value === undefined) return EM_DASH;
  const date = toDate(value);
  if (!date) return EM_DASH;
  return dayMonthYear(date, zone(options));
}

function dayMonthYear(date: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat(MONTH_LOCALE, { day: 'numeric', month: 'short', year: 'numeric', timeZone }).formatToParts(date);
  const get = (type: string): string => parts.find((part) => part.type === type)?.value ?? '';
  return `${get('day')} ${get('month')} ${get('year')}`;
}

/** `09:00` (24-hour) in the given timezone. */
export function formatTime(value: string | number | Date | null | undefined, options?: TimeFormatOptions): string {
  if (value === null || value === undefined) return EM_DASH;
  const date = toDate(value);
  if (!date) return EM_DASH;
  return new Intl.DateTimeFormat(options?.locale ?? LOCALE, {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: zone(options),
  }).format(date);
}

/**
 * The short name of a timezone at an instant — `UTC`, `BST`, `GMT+4`. Times are always shown
 * with their zone (04-data §4), so this is appended by formatDateTime and stated by feeds.
 */
export function timeZoneLabel(timeZone: string, at: Date = new Date(), locale = LOCALE): string {
  try {
    const parts = new Intl.DateTimeFormat(locale, { timeZone, timeZoneName: 'short' }).formatToParts(at);
    const name = parts.find((part) => part.type === 'timeZoneName')?.value;
    return name ?? timeZone;
  } catch {
    return timeZone;
  }
}

/** `16 Sep 2026, 09:00 UTC` — the absolute instant with its zone, for titles and accessible names. */
export function formatDateTime(value: string | number | Date | null | undefined, options?: TimeFormatOptions): string {
  if (value === null || value === undefined) return EM_DASH;
  const date = toDate(value);
  if (!date) return EM_DASH;
  const timeZone = zone(options);
  return `${formatDate(date, { ...options, timeZone })}, ${formatTime(date, { ...options, timeZone })} ${timeZoneLabel(timeZone, date, options?.locale)}`;
}

/**
 * Formats a calendar date (`YYYY-MM-DD`, no instant) without a timezone shift: a due date of
 * 18 September is 18 September in every zone. Invalid input renders the em dash.
 */
export function formatDateOnly(value: string | null | undefined): string {
  const parts = parseDateOnly(value);
  if (!parts) return EM_DASH;
  const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
  return dayMonthYear(date, 'UTC');
}

export interface DateOnlyParts {
  year: number;
  month: number;
  day: number;
}

/** Parses `YYYY-MM-DD` strictly; rejects impossible dates such as 2026-02-30. */
export function parseDateOnly(value: string | null | undefined): DateOnlyParts | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const check = new Date(Date.UTC(year, month - 1, day));
  if (check.getUTCFullYear() !== year || check.getUTCMonth() !== month - 1 || check.getUTCDate() !== day) return null;
  return { year, month, day };
}

/** Today's calendar date (`YYYY-MM-DD`) in the given timezone. */
export function todayDateOnly(now: Date = new Date(), timeZone: string = browserTimeZone()): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
  const get = (type: string): string => parts.find((part) => part.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** Whole days from `today` to `date` (negative when the date has passed). Both `YYYY-MM-DD`. */
export function daysBetween(today: string, date: string): number | null {
  const a = parseDateOnly(today);
  const b = parseDateOnly(date);
  if (!a || !b) return null;
  const ms = Date.UTC(b.year, b.month - 1, b.day) - Date.UTC(a.year, a.month - 1, a.day);
  return Math.round(ms / 86_400_000);
}

export type DueState = 'overdue' | 'today' | 'soon' | 'later';

/** Classifies a due date against today: overdue, today, within the next 7 days, or later. */
export function dueState(date: string | null | undefined, today: string): DueState | null {
  if (!date) return null;
  const days = daysBetween(today, date);
  if (days === null) return null;
  if (days < 0) return 'overdue';
  if (days === 0) return 'today';
  if (days <= 7) return 'soon';
  return 'later';
}

/** `Due today`, `Overdue by 3 days`, `Due in 2 days`, `Due 18 Oct 2026`. */
export function formatDue(date: string | null | undefined, today: string): string {
  if (!date) return EM_DASH;
  const days = daysBetween(today, date);
  if (days === null) return EM_DASH;
  if (days === 0) return 'Due today';
  if (days === 1) return 'Due tomorrow';
  if (days === -1) return 'Overdue by 1 day';
  if (days < 0) return `Overdue by ${formatCount(-days)} days`;
  if (days <= 7) return `Due in ${formatCount(days)} days`;
  return `Due ${formatDateOnly(date)}`;
}

/**
 * Relative time for recency ("3 days ago", "in 2 hours", "just now"). The caller renders the
 * absolute value beside it in a title and an accessible name (04-data §4) — this never stands alone.
 */
export function formatRelative(value: string | number | Date | null | undefined, now: Date = new Date(), locale = LOCALE): string {
  if (value === null || value === undefined) return EM_DASH;
  const date = toDate(value);
  if (!date) return EM_DASH;
  const diffSeconds = Math.round((date.getTime() - now.getTime()) / 1000);
  const abs = Math.abs(diffSeconds);
  if (abs < 45) return 'just now';
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 31_536_000],
    ['month', 2_592_000],
    ['week', 604_800],
    ['day', 86_400],
    ['hour', 3_600],
    ['minute', 60],
  ];
  for (const [unit, seconds] of units) {
    if (abs >= seconds) {
      const amount = Math.round(diffSeconds / seconds);
      return rtf.format(amount, unit);
    }
  }
  return rtf.format(diffSeconds, 'second');
}

/** Human duration: `45s`, `1m 20s`, `1h 05m`, `2d 3h`. Sub-second values round to `0s`. */
export function formatDuration(totalSeconds: number | null | undefined): string {
  if (totalSeconds === null || totalSeconds === undefined || !Number.isFinite(totalSeconds) || totalSeconds < 0) return EM_DASH;
  const seconds = Math.round(totalSeconds);
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  if (minutes > 0) return `${minutes}m ${String(rest).padStart(2, '0')}s`;
  return `${rest}s`;
}

/** Transcript offset as `m:ss` or `h:mm:ss`, for `[mm:ss]` style labels and `#t-<index>` anchors. */
export function formatTimestamp(totalSeconds: number | null | undefined): string {
  if (totalSeconds === null || totalSeconds === undefined || !Number.isFinite(totalSeconds) || totalSeconds < 0) return EM_DASH;
  const seconds = Math.floor(totalSeconds);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;
  const mmss = `${hours > 0 ? String(minutes).padStart(2, '0') : String(minutes)}:${String(rest).padStart(2, '0')}`;
  return hours > 0 ? `${hours}:${mmss}` : mmss;
}

/** Locale-grouped integer/decimal: `1,234`. */
export function formatCount(value: number | null | undefined, locale = LOCALE): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return EM_DASH;
  return new Intl.NumberFormat(locale).format(value);
}

/** Abbreviated large number for stat tiles (`1.2M`); the exact value goes in the title/name. */
export function formatCompact(value: number | null | undefined, locale = LOCALE): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return EM_DASH;
  if (Math.abs(value) < 10_000) return formatCount(value, locale);
  // Why `en`: en-GB abbreviates a million as "m", which reads as metres; the standard shows "1.2M".
  return new Intl.NumberFormat(MONTH_LOCALE, { notation: 'compact', maximumFractionDigits: 1 }).format(value);
}

/**
 * A cost held as integer micro-USD, shown with its currency code and two decimals (04-data §4:
 * always with the currency): `USD 1,234.50`. Null — a call whose model the rate card did not know —
 * renders the em dash, distinct from `USD 0.00`, which is a priced call that cost nothing. A priced
 * call worth less than a cent — a small judge call on a cheap model — keeps up to six decimals
 * (`USD 0.0025`), so money spent never reads as nothing beside a genuinely free call.
 */
export function formatUsd(microUsd: number | null | undefined, locale = LOCALE): string {
  if (microUsd === null || microUsd === undefined || !Number.isFinite(microUsd)) return EM_DASH;
  const usd = microUsd / 1_000_000;
  const subCent = usd !== 0 && Math.abs(usd) < 0.01;
  // Why the replacement: ICU sets a no-break space between the code and the figure; a plain space keeps
  // the string stable across builds and matchable in tests, and the value never wraps in a tile anyway.
  return new Intl.NumberFormat(locale, { style: 'currency', currency: 'USD', currencyDisplay: 'code', minimumFractionDigits: 2, maximumFractionDigits: subCent ? 6 : 2 })
    .format(usd)
    .replace(/[\u00A0\u202F]/gu, ' ');
}

/** Count badges cap at `99+` (03-components §9). */
export function formatBadgeCount(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '0';
  return value > 99 ? '99+' : String(Math.floor(value));
}

/**
 * Plural by rule, never by appending "s" (05-content §8). `forms.other` is required; `one` is used
 * for the singular category of the locale. The count is inserted for `#`.
 */
export function pluralise(count: number, forms: { one: string; other: string; zero?: string }, locale = LOCALE): string {
  if (count === 0 && forms.zero) return forms.zero.replace('#', formatCount(0, locale));
  const category = new Intl.PluralRules(locale).select(count);
  const form = category === 'one' ? forms.one : forms.other;
  return form.replace('#', formatCount(count, locale));
}

/**
 * A list joined as the locale joins one — "a, b and c" (05-content §8): the conjunction and its commas are the
 * locale's, never spliced in by hand. Each item is a complete phrase, and the sentence around the list is one complete
 * string with the list as its placeholder.
 */
export function formatList(items: readonly string[], locale = LOCALE): string {
  return new Intl.ListFormat(locale, { type: 'conjunction', style: 'long' }).format(items);
}

/**
 * What the locale puts before each item of a list — `['', ', ', ' and ']` for three in English — for a list whose items
 * are elements (links) rather than words, joined exactly as `formatList` joins words (05-content §8; the Phase 1c audit's
 * D30: the project page's "Concerns" links were joined with ", " by hand). Text after the last item, where a locale has
 * any, is not returned.
 */
export function listSeparators(count: number, locale = LOCALE): string[] {
  if (count <= 0) return [];
  const parts = new Intl.ListFormat(locale, { type: 'conjunction', style: 'long' }).formatToParts(Array.from({ length: count }, (_, index) => `item${index}`));
  const separators: string[] = [];
  let pending = '';
  for (const part of parts) {
    if (part.type === 'element') {
      separators.push(pending);
      pending = '';
    } else {
      pending += part.value;
    }
  }
  return separators;
}

/**
 * A list of names with those it does not name counted as its last item — "Ada, Bob and 2 unnamed attendees", "Ada, Bob and
 * 3 more members" — the count's words chosen whole by `pluralise`, the list joined by the locale. Why (05-content §8; the
 * Phase 1c audit's D30): the sites joined names with ", " and spliced " and N more" on by condition. With nothing left
 * over it is `formatList` alone.
 */
export function formatListWithRest(items: readonly string[], rest: number, restForms: { one: string; other: string }, locale = LOCALE): string {
  if (rest <= 0) return formatList(items, locale);
  return formatList([...items, pluralise(rest, restForms, locale)], locale);
}

/**
 * A choice among items joined as the locale joins one — "a, b or c" (05-content §8), `formatList`'s disjunction. Why a
 * helper of its own: a closed vocabulary said in a sentence ("calm, mixed, strained or tense") is a list too, and its
 * "or" and commas are the locale's as much as "and" is.
 */
export function formatAlternatives(items: readonly string[], locale = LOCALE): string {
  return new Intl.ListFormat(locale, { type: 'disjunction', style: 'long' }).format(items);
}

/** Percentage with a stated precision: `42%`. The caller states the base where it is not obvious. */
export function formatPercent(fraction: number | null | undefined, digits = 0, locale = LOCALE): string {
  if (fraction === null || fraction === undefined || !Number.isFinite(fraction)) return EM_DASH;
  return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: digits }).format(fraction);
}

/** Splits a list of ISO instants into day groups (viewer's zone), preserving the input order. */
export function groupByDay<T>(items: readonly T[], instantOf: (item: T) => string, timeZone: string): Array<{ day: string; label: string; items: T[] }> {
  const groups: Array<{ day: string; label: string; items: T[] }> = [];
  for (const item of items) {
    const date = toDate(instantOf(item));
    const day = date ? todayDateOnly(date, timeZone) : 'unknown';
    const last = groups[groups.length - 1];
    if (last && last.day === day) {
      last.items.push(item);
    } else {
      groups.push({ day, label: date ? formatDate(date, { timeZone }) : 'Unknown date', items: [item] });
    }
  }
  return groups;
}
